/**
 * Simple Audio Player - Estilo BPM Supreme
 * Diseño minimalista con colores de WTPL
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SimpleAudioPlayer = ({ fileId, trackTitle, artist, onClose, trackInfo = {}, autoPlay = true }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;

  // Build streaming URL with auth token
  const streamUrl = useMemo(() => {
    if (!fileId) return null;
    const token = localStorage.getItem('wtpl_session_token');
    return token ? `${API}/stream/${fileId}?token=${encodeURIComponent(token)}` : `${API}/stream/${fileId}`;
  }, [fileId]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    if (isNaN(seconds) || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle time update
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  // Handle loaded metadata
  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setIsLoading(false);
    }
  }, []);

  // Handle can play - auto play when ready
  const handleCanPlay = useCallback(() => {
    setIsLoading(false);
    setError(null);
    
    // Auto play when audio is ready
    if (autoPlay && audioRef.current) {
      audioRef.current.play().catch(e => {
        console.error('AutoPlay error:', e);
        // Some browsers block autoplay, user needs to click
      });
    }
  }, [autoPlay]);

  // Handle error with retry
  const handleError = useCallback((e) => {
    console.error('Audio error:', e, 'Retry:', retryCount);
    
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.load();
        }
      }, 1000 * (retryCount + 1));
      return;
    }
    
    setError('Error al cargar');
    setIsLoading(false);
  }, [retryCount]);

  // Toggle play/pause
  const togglePlay = useCallback(() => {
    if (!audioRef.current || error) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(e => {
        console.error('Play error:', e);
        setError('Error al reproducir');
      });
    }
  }, [isPlaying, error]);

  // Handle seek
  const handleSeek = useCallback((e) => {
    if (!audioRef.current || !duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;
    
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // Handle volume change
  const handleVolumeChange = useCallback((e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  }, []);

  // Set volume on mount
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
    }
  }, [volume]);

  // Progress percentage
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        togglePlay();
      } else if (e.code === 'Escape') {
        onClose?.();
      } else if (e.code === 'ArrowLeft' && audioRef.current) {
        audioRef.current.currentTime = Math.max(0, currentTime - 5);
      } else if (e.code === 'ArrowRight' && audioRef.current) {
        audioRef.current.currentTime = Math.min(duration, currentTime + 5);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, onClose, currentTime, duration]);

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-[#0D0D0D] border-t border-[#1A1A1A]">
      {/* Hidden Audio Element */}
      <audio
        ref={audioRef}
        src={streamUrl}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onCanPlay={handleCanPlay}
        onError={handleError}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onEnded={() => setIsPlaying(false)}
        preload="auto"
      />

      {/* Progress Bar - Clickable */}
      <div 
        className="h-1 bg-[#1A1A1A] cursor-pointer group"
        onClick={handleSeek}
      >
        <div 
          className="h-full bg-gradient-to-r from-[#00D9FF] to-[#00D9FF] transition-all duration-100 relative"
          style={{ width: `${progress}%` }}
        >
          {/* Hover indicator */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg" />
        </div>
      </div>

      {/* Main Player Content */}
      <div className="flex items-center justify-between px-4 py-3 max-w-screen-2xl mx-auto">
        {/* Left: Track Info */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Play/Pause Button */}
          <button
            onClick={togglePlay}
            disabled={isLoading || !!error}
            className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
              isLoading 
                ? 'bg-[#1A1A1A] cursor-wait' 
                : error 
                ? 'bg-red-500/20 cursor-not-allowed'
                : 'bg-gradient-to-r from-[#00D9FF] to-[#00D9FF] hover:opacity-90'
            }`}
            data-testid="simple-player-play-btn"
          >
            {isLoading ? (
              <svg className="w-5 h-5 text-[#555] animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : error ? (
              <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
            ) : isPlaying ? (
              <svg className="w-5 h-5 text-black" fill="currentColor" viewBox="0 0 24 24">
                <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
              </svg>
            ) : (
              <svg className="w-5 h-5 text-black ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8 5v14l11-7z"/>
              </svg>
            )}
          </button>

          {/* Track Info */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <p className="text-white font-medium text-sm truncate" title={trackTitle}>
                {trackTitle || 'Sin título'}
              </p>
              {trackInfo?.bpm && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#00D9FF]/20 text-[#00D9FF] rounded font-bold flex-shrink-0">
                  {trackInfo.bpm} BPM
                </span>
              )}
              {trackInfo?.key && (
                <span className="text-[10px] px-1.5 py-0.5 bg-[#00D9FF]/20 text-[#00D9FF] rounded font-bold flex-shrink-0">
                  {trackInfo.key}
                </span>
              )}
            </div>
            <p className="text-[#666] text-xs truncate" title={artist}>
              {artist || trackInfo?.artist || 'Artista desconocido'}
            </p>
          </div>
        </div>

        {/* Center: Time Display */}
        <div className="flex items-center gap-2 text-xs font-mono mx-4">
          <span className="text-white w-10 text-right">{formatTime(currentTime)}</span>
          <span className="text-[#444]">/</span>
          <span className="text-[#666] w-10">{formatTime(duration)}</span>
        </div>

        {/* Right: Volume & Close */}
        <div className="flex items-center gap-3">
          {/* Volume Control */}
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => setVolume(volume === 0 ? 0.8 : 0)}
              className="text-[#666] hover:text-white transition-colors"
            >
              {volume === 0 ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                </svg>
              )}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={handleVolumeChange}
              className="w-20 h-1 appearance-none bg-[#262626] rounded-full cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer"
            />
          </div>

          {/* Error Retry */}
          {error && (
            <button
              onClick={() => {
                setError(null);
                setRetryCount(0);
                setIsLoading(true);
                audioRef.current?.load();
              }}
              className="text-xs text-[#00D9FF] hover:underline px-2"
            >
              Reintentar
            </button>
          )}

          {/* Close Button */}
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-[#262626] flex items-center justify-center text-[#666] hover:text-white transition-all"
            data-testid="simple-player-close-btn"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SimpleAudioPlayer;
